import AdManager from '../ads/ad-manager.js';
import Piano from '../piano/piano.js';
import InstantSearch from '../algolia/instantsearch';

class SourcePoint {
	constructor() {
		this.grants = [];
		this.privacyManagerID = window.bi_sourcepoint?.privacy_manager_id || '';
		this.vendors = window.bi_sourcepoint?.vendors || [];
	}

	init() {
		if (typeof __tcfapi === 'undefined') {
			return;
		}

		const self = this;

		// Get custom vendor consent data
		__tcfapi('addEventListener', 2, function (tcData) {
			// Check custom vendor consent data when loaded and when privacy settings are altered
			if (
				tcData.eventStatus === 'tcloaded' ||
				tcData.eventStatus === 'useractioncomplete'
			) {
				// Load scripts from vendors
				AdManager.maybeLoadOutbrain();
				AdManager.maybeLoadNativendo();
				AdManager.maybeLoadCleverPush();
				AdManager.maybeLoadConnatix();
				AdManager.maybeLoadApester();

				self.getSocialEmbeds();
				self.getImages();
				self.getScripts();
				self.addEventListeners();

				__tcfapi('getCustomVendorConsents', 2, function (data) {
					// Store vendor data in BI.Sourcepoint.grants for later use
					self.grants = data.grants;

					// Maybe load Piano based on consent
					Piano.maybeInit();

					// Maybe load Algolia InstantSearch based on consent
					new InstantSearch();
				});
			}
		});
	}

	maybeLoadVendors() {
		// activate all blocked social embeds and scripts
		this.activateSocialEmbeds();
		this.activateImages();
		this.activateScripts();
	}

	decodeContent(content) {
		// Decode Base64
		const decodedBase64 = atob(content);

		// Decode UTF-8
		const utf8Decoder = new TextDecoder('utf-8');
		return utf8Decoder.decode(
			new Uint8Array([...decodedBase64].map((char) => char.charCodeAt(0))),
		);
	}

	vendorHasGrant(vendorSearchTerm) {
		const vendorBySearchTerm = this.vendors.find((vendor) =>
			vendor.search_terms.some(
				(search_terms) => search_terms.search_term === vendorSearchTerm,
			),
		);

		if (typeof vendorBySearchTerm === 'undefined') {
			return false;
		}

		if (
			this.grants[vendorBySearchTerm.vendor_id].vendorGrant === undefined ||
			!this.grants[vendorBySearchTerm.vendor_id].vendorGrant
		) {
			return false;
		}

		return true;
	}

	resetElements() {
		const self = this;
		self.getSocialEmbeds();
		self.getImages();
		self.addEventListeners();

		__tcfapi('getCustomVendorConsents', 2, function (data) {
			// Store vendor data in BI.Sourcepoint.grants for later use
			self.grants = data.grants;

			// activate all blocked social embeds and scripts
			self.activateSocialEmbeds();
			self.initializeSocialEmbeds();
			self.activateImages();
		});
	}

	activateSocialEmbeds() {
		const self = this;

		if (self.socialEmbeds.length === 0) {
			return;
		}

		jQuery.each(self.socialEmbeds, function (i, object) {
			let consent = true;
			// eslint-disable-next-line no-unused-vars
			let hasVendorWithoutConsent = false;

			const decodedEmbed = self.decodeContent(object.embedData);
			if (self.vendors.length > 0) {
				// For each social embed, check if we have consent
				jQuery.each(self.vendors, function (x, vendor) {
					const consentObject = self.hasVendorConsent(
						self,
						decodedEmbed,
						'',
						vendor.search_terms,
						vendor.vendor_id,
					);
					if (!consentObject.consent) {
						consent = false;
					}

					if (consentObject.vendorFound && !consentObject.consent) {
						hasVendorWithoutConsent = true;
					}
				});
			}

			if (!consent) {
				return;
			}

			self.prepareSocialEmbeds();
			object.element.replaceWith(decodedEmbed);
		});
	}

	/**
	 * Some social embeds need to be prepared before being loaded
	 */
	prepareSocialEmbeds() {
		// Prepare Flourish embeds
		// See: https://developers.flourish.studio/embedding/controlling-when-graphics-load/
		window.Flourish = {};
	}

	/**
	 * Some social embeds need to be reinitialized after being loaded in premium content
	 */
	initializeSocialEmbeds() {
		// Reinitialize Instagram embeds
		if (
			typeof window.instgrm !== 'undefined' &&
			typeof window.instgrm.Embeds !== 'undefined'
		) {
			window.instgrm.Embeds.process();
		}

		// Reinitialize Flourish embeds
		if (typeof window.Flourish !== 'undefined') {
			// Reset the loaded state to make it possible to reinitalize Flourish
			window.FlourishLoaded = null;

			// Find all Flourish embeds on the page and load them
			const flourishEmbeds = document.getElementsByClassName('flourish-embed');
			flourishEmbeds.forEach(function (flourishEmbed) {
				if (typeof window.Flourish.loadEmbed !== 'undefined') {
					window.Flourish.loadEmbed(flourishEmbed);
				}
			});
		}
	}

	activateImages() {
		const self = this;
		if (self.images.length === 0) {
			return;
		}

		jQuery.each(self.images, function (i, object) {
			let consent = true;
			// eslint-disable-next-line no-unused-vars
			let hasVendorWithoutConsent = false;

			const decodedImage = self.decodeContent(object.image);
			if (self.vendors.length > 0) {
				// For each image, check if we have consent
				jQuery.each(self.vendors, function (x, vendor) {
					const consentObject = self.hasVendorConsent(
						self,
						decodedImage,
						object.name,
						vendor.search_terms,
						vendor.vendor_id,
					);
					if (!consentObject.consent) {
						consent = false;
					}

					if (consentObject.vendorFound && !consentObject.consent) {
						hasVendorWithoutConsent = true;
					}
				});
			}

			if (!consent) {
				return;
			}

			object.element.replaceWith(decodedImage);
		});
	}

	activateScripts() {
		const self = this;

		if (self.scripts.length === 0) {
			return;
		}

		jQuery.each(self.scripts, function (i, object) {
			let consent = true;
			let hasVendorWithoutConsent = false;

			if (self.vendors.length > 0) {
				// For each script, check if we have consent
				jQuery.each(self.vendors, function (x, vendor) {
					const consentObject = self.hasVendorConsent(
						self,
						object.script,
						object.name,
						vendor.search_terms,
						vendor.vendor_id,
					);
					if (!consentObject.consent) {
						consent = false;
					}

					if (consentObject.vendorFound && !consentObject.consent) {
						hasVendorWithoutConsent = true;
					}
				});
			}

			if (!consent || hasVendorWithoutConsent) {
				return;
			}

			const script = document.createElement('script');

			// Check if the script should be loaded as module or noModule.
			let scriptType = 'text/javascript';
			if (object.isModule) {
				scriptType = 'module';
			}
			if (object.noModule) {
				script.noModule = true;
			}

			// Add additional attributes if available
			script.type = scriptType;
			script.innerHTML = object.script;
			if (object.id) {
				script.setAttribute('id', object.id);
			}
			if (object.src) {
				script.setAttribute('src', object.src);
			}

			// Set the sp-name on the activated script so we can recognise it in acceptance tests.
			script.setAttribute('data-sp-name', object.name);

			object.element.replaceWith(script);
		});
	}

	// Check if content is from a specific vendor and check if user has given consent to the vendor
	hasVendorConsent(self, content, name, vendorSearchTerms, vendorId) {
		// Check if the embed/script/image contains content from the vendor
		let vendorFound = false;
		vendorSearchTerms.forEach(function (object) {
			const regex = new RegExp(object.search_term, 'i');
			if (content.match(regex)) {
				vendorFound = true;
			}
			if (name.match(regex)) {
				vendorFound = true;
			}
		});

		if (!vendorFound) {
			// The content is not from this vendor
			// Consent is true by default, it could be a harmless embed/script
			return {
				consent: true,
				vendorFound: false,
			};
		}

		// Cannot find consent data for this vendor
		if (
			self.grants[vendorId] === undefined ||
			self.grants[vendorId].vendorGrant === undefined
		) {
			return {
				consent: false,
				vendorFound: true,
			};
		}

		// The user has not given consent
		if (!self.grants[vendorId].vendorGrant) {
			return {
				consent: false,
				vendorFound: true,
			};
		}

		// The content is from the vendor and the user has given consent
		return {
			consent: true,
			vendorFound: true,
		};
	}

	// Store all sourcepoint placeholders in a const for better security.
	getSocialEmbeds() {
		const socialEmbeds = [];
		const $placeholders = jQuery('.sourcepoint__social-placeholder');
		$placeholders.each(function (i, el) {
			socialEmbeds.push({
				embedData: jQuery(el).data('embed'),
				element: jQuery(el),
			});
		});

		Object.defineProperty(this, 'socialEmbeds', {
			value: socialEmbeds,
			writable: false,
			enumerable: false,
			configurable: true,
		});
	}

	getImages() {
		const images = [];
		const $placeholders = jQuery('.sourcepoint__image-placeholder');
		$placeholders.each(function (i, el) {
			images.push({
				image: jQuery(el).data('image'),
				name: jQuery(el).data('sp-name'),
				element: jQuery(el),
			});
		});

		Object.defineProperty(this, 'images', {
			value: images,
			writable: false,
			enumerable: false,
			configurable: true,
		});
	}

	getScripts() {
		const scripts = [];
		const blockedScripts = jQuery('[data-sp-type=blocked-script]');
		blockedScripts.each(function (i, el) {
			scripts.push({
				script: jQuery(el).text(),
				name: jQuery(el).data('sp-name'),
				isModule: jQuery(el).data('sp-is-module') || false,
				noModule: jQuery(el).attr('nomodule') !== undefined || false,
				element: jQuery(el),
				src: jQuery(el).attr('src'),
				id: jQuery(el).attr('id'),
			});
		});

		// Store all scripts in a const for better security.
		Object.defineProperty(this, 'scripts', {
			value: scripts,
			writable: false,
			enumerable: false,
			configurable: true,
		});
	}

	addEventListeners() {
		const rejectButton = document.querySelector('.bi-sp-reject-all');

		rejectButton?.addEventListener('click', () => {
			__tcfapi('postRejectAll', 2, function () {
				const popup = document.querySelector('.reject-popup');
				const okButton = popup?.querySelector('button');
				popup?.classList.add('reject-popup--show');

				okButton?.addEventListener('click', () => {
					window.location.reload();
				});
			});
		});

		this.addAlgoliaEventListeners();
		this.addSocialEmbedEventListeners();
		this.addPianoEventListeners();
	}

	addAlgoliaEventListeners() {
		const algoliaVendors = window.bi_sourcepoint?.algolia_vendors || [];
		const algoliaButtons = document.querySelectorAll(
			'.sourcepoint__algolia-placeholder button',
		);

		algoliaButtons?.forEach((button) => {
			button.addEventListener('click', async () => {
				// Get the vendor ids
				const vendorIDs = (algoliaVendors?.vendor_ids || []).map(
					(vendor) => vendor.vendor_id,
				);

				// Get the purpose grants for each vendor
				let purposeGrants = (vendorIDs || []).map(
					(vendorID) => this.grants[vendorID]?.purposeGrants,
				);

				// Remove undefined values from the array, this happens when a vendor is not in grants list or has no purpose grants
				purposeGrants = purposeGrants.filter(
					(purposeGrant) => purposeGrant !== undefined,
				);

				// Create an array with all unique purpose ids from the purpose grants
				const purposeIDs = [
					...new Set(purposeGrants.flatMap((object) => Object.keys(object))),
				];

				await this.postCustomConsent(vendorIDs, purposeIDs, []);

				// Activate Algolia elements
				document.querySelector('.sourcepoint__algolia-placeholder').remove();
				new InstantSearch();
			});
		});
	}

	addSocialEmbedEventListeners() {
		const socialEmbedVendors =
			window.bi_sourcepoint?.social_embed_vendors || [];
		const socialEmbedButtons = document.querySelectorAll(
			'.sourcepoint__social-placeholder button',
		);

		socialEmbedButtons?.forEach((button) => {
			button.addEventListener('click', async () => {
				// Get the vendor ids
				const vendorIDs = (socialEmbedVendors?.vendor_ids || []).map(
					(vendor) => vendor.vendor_id,
				);

				// Get the purpose grants for each vendor
				let purposeGrants = (vendorIDs || []).map(
					(vendorID) => this.grants[vendorID]?.purposeGrants,
				);

				// Remove undefined values from the array, this happens when a vendor is not in grants list or has no purpose grants
				purposeGrants = purposeGrants.filter(
					(purposeGrant) => purposeGrant !== undefined,
				);

				// Create an array with all unique purpose ids from the purpose grants
				const purposeIDs = [
					...new Set(purposeGrants.flatMap((object) => Object.keys(object))),
				];

				await this.postCustomConsent(vendorIDs, purposeIDs, []);

				// Activate the embeds
				this.activateSocialEmbeds();
			});
		});
	}

	addPianoEventListeners() {
		const pianoVendors = window.bi_sourcepoint?.piano_vendors || [];
		const pianoButtons = document.querySelectorAll(
			'.sourcepoint__piano-placeholder button',
		);

		pianoButtons?.forEach((button) => {
			button.addEventListener('click', async () => {
				// Get the vendor ids
				const vendorIDs = (pianoVendors?.vendor_ids || []).map(
					(vendor) => vendor.vendor_id,
				);

				// Get the purpose grants for each vendor
				let purposeGrants = (vendorIDs || []).map(
					(vendorID) => this.grants[vendorID]?.purposeGrants,
				);

				// Remove undefined values from the array, this happens when a vendor is not in grants list or has no purpose grants
				purposeGrants = purposeGrants.filter(
					(purposeGrant) => purposeGrant !== undefined,
				);

				// Create an array with all unique purpose ids from the purpose grants
				const purposeIDs = [
					...new Set(purposeGrants.flatMap((object) => Object.keys(object))),
				];

				await this.postCustomConsent(vendorIDs, purposeIDs, []);

				// Activate Piano elements
				document.querySelector('.sourcepoint__piano-placeholder').remove();
				Piano.maybeInit();
			});
		});
	}

	postCustomConsent(vendorIDs, purposeIDs, liPurposeIds) {
		return new Promise((resolve, reject) => {
			__tcfapi(
				'postCustomConsent',
				2,
				(data, success) => {
					// Update the grants
					this.grants = data.grants;

					success ? resolve('Success!') : reject('Failed!');
				},
				vendorIDs,
				purposeIDs,
				liPurposeIds,
			);
		});
	}
}

const instance = new SourcePoint();
export default instance;
